import { forwardRef } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../utils';
import {
  emptyStateActionsRecipe,
  emptyStateDescriptionRecipe,
  emptyStateRootRecipe,
  emptyStateTextRecipe,
  emptyStateTitleRecipe,
} from './EmptyState.recipe';

import type { EmptyStateProps } from './types';

const ICON_SIZE = { inline: 32, page: 48 } as const;

/**
 * Whether a node would render nothing, so its wrapper can be left out and its gap with it.
 *
 * `actions={canCreate && <Button/>}` hands React `false`; a `!== undefined` check would already have
 * rendered the wrapper and left a 16px gap with nothing in it. `0` is NOT absent (it is a value), and
 * an array is recursed. `ContentBlock` carries the same helper for the same reason, at more length.
 */
const isEmptyNode = (node: ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === 'boolean' || node === '') return true;

  return Array.isArray(node) && node.every((child) => isEmptyNode(child as ReactNode));
};

/**
 * Design System 2.0 empty state: what is missing, why, and what to do next.
 *
 * Use it when the region has NOTHING else to show. If content is still there and something beside it
 * failed, that is an `Alert`. A "no results" row inside a dropdown belongs to `Menu` / `Select`.
 *
 * It is presentational and owns no behaviour. It sets no `role`, no live region and no focus:
 *
 *  - **Announcements** are the host's. A list or table announces its new result count politely; a
 *    first-use empty state is not announced on page load.
 *  - **Focus** is the page template's: it focuses the `h1` (through `headingRef`) when a terminal page
 *    state first mounts, never on a refetch of a state already showing.
 *  - **Retry** is the host's: the Button shows `loading` while it retries.
 *
 * Figma set 2072:139929. See `EmptyState.recipe.ts` for the measured geometry.
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ actions, className, description, headingLevel, headingRef, icon: Icon, size = 'inline', title, ...rest }, ref) => {
    if (title.trim() === '') {
      throw new Error('EmptyState requires a non-empty title.');
    }

    // A template rather than a lookup table: the level is constrained to 1-6 by the props type.
    const Heading = `h${headingLevel}` as const;

    const hasDescription = !isEmptyNode(description);
    const hasActions = !isEmptyNode(actions);

    return (
      <div
        ref={ref}
        className={cn(emptyStateRootRecipe({ size }), className)}
        {...rest}
        data-part="root"
        data-size={size}
      >
        {Icon && (
          <span className="flex shrink-0" data-part="icon">
            <Icon size={ICON_SIZE[size]} />
          </span>
        )}

        <div className={emptyStateTextRecipe({ size })} data-part="text">
          <Heading
            ref={headingRef}
            className={emptyStateTitleRecipe({ size })}
            data-part="title"
            tabIndex={headingRef ? -1 : undefined}
          >
            {title}
          </Heading>

          {hasDescription && (
            <p className={emptyStateDescriptionRecipe({ size })} data-part="description">
              {description}
            </p>
          )}
        </div>

        {hasActions && (
          <div className={emptyStateActionsRecipe()} data-part="actions">
            {actions}
          </div>
        )}
      </div>
    );
  }
);
